import {
  Box,
  IconButton,
  InputAdornment,
  InputBase,
  Paper,
  alpha,
  useTheme,
} from "@mui/material";
import { Clear, Search } from "@mui/icons-material";
import { useEffect, useState } from "react";

const DatasetViewerSearch = ({
  searchString,
  onSearchChange,
  placeholder = "Search dataset content...",
}: {
  searchString: string;
  onSearchChange: (value: string) => void;
  placeholder?: string;
}) => {
  const theme = useTheme();
  const [localSearchValue, setLocalSearchValue] = useState(searchString);
  const [isFocused, setIsFocused] = useState(false);

  // Sync local value with external searchString when it changes from outside (e.g., clear from close button)
  useEffect(() => {
    setLocalSearchValue(searchString);
  }, [searchString]);

  const handleClear = () => {
    setLocalSearchValue("");
    onSearchChange("");
  };

  const handleSubmit = () => {
    if (localSearchValue.trim()) {
      onSearchChange(localSearchValue);
    }
  };

  return (
    <Paper
      data-testid="sdr-search-input"
      elevation={0}
      sx={{
        display: "flex",
        alignItems: "center",
        width: "100%",
        height: "44px",
        border: `1px solid ${
          isFocused ? theme.palette.primary.main : theme.palette.divider
        }`,
        borderRadius: "8px",
        backgroundColor: isFocused
          ? theme.palette.background.paper
          : alpha(theme.palette.background.default, 0.5),
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          backgroundColor: theme.palette.background.paper,
          borderColor: isFocused
            ? theme.palette.primary.main
            : alpha(theme.palette.primary.main, 0.3),
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          pl: 1.5,
          pr: 0.5,
        }}
      >
        <Search
          sx={{
            fontSize: "1.25rem",
            color: isFocused
              ? theme.palette.primary.main
              : theme.palette.text.secondary,
            transition: "color 0.2s ease-in-out",
          }}
        />
      </Box>
      <InputBase
        data-testid="sdr-search-input-inner"
        data-attr="datasets:input:dataset-viewer-search"
        placeholder={placeholder}
        inputProps={{ "aria-label": "search for source data records" }}
        fullWidth
        value={localSearchValue}
        onChange={(e) => setLocalSearchValue(e.target.value)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            handleSubmit();
          }
        }}
        sx={{
          fontSize: "0.875rem",
          "& input": {
            padding: "8px 4px",
            "&::placeholder": {
              color: theme.palette.text.secondary,
              opacity: 0.7,
            },
          },
        }}
        endAdornment={
          <InputAdornment position="end">
            {localSearchValue && (
              <IconButton
                onClick={handleClear}
                edge="end"
                size="small"
                data-testid="sdr-btn-search-clear"
                data-attr="datasets:btn:clear-viewer-search"
                sx={{
                  mr: 0.5,
                  color: theme.palette.text.secondary,
                  "&:hover": {
                    color: theme.palette.error.main,
                    backgroundColor: alpha(theme.palette.error.main, 0.08),
                  },
                }}
              >
                <Clear fontSize="small" />
              </IconButton>
            )}
            <IconButton
              onClick={handleSubmit}
              edge="end"
              size="small"
              data-testid="sdr-btn-search"
              data-attr="datasets:btn:submit-viewer-search"
              disabled={!localSearchValue.trim()}
              sx={{
                mr: 0.5,
                color: localSearchValue.trim()
                  ? theme.palette.primary.main
                  : theme.palette.text.disabled,
                "&:hover": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                },
                "&:disabled": {
                  color: theme.palette.text.disabled,
                },
              }}
            >
              <Search fontSize="small" />
            </IconButton>
          </InputAdornment>
        }
      />
    </Paper>
  );
};

export default DatasetViewerSearch;
